import type {
  AjustesDePlano,
  AvisoDocumento,
  AvisoMiolo,
  ConfigPlanner,
  DocumentoAcademico,
  FormatoDoTexto,
  ModeloDeDocumento,
  PeriodoCota,
  Plano as PlanoDoCatalogo,
  ReferenciaDocumento,
  ResumoMiolo,
  TipoCobranca,
} from '@miolos/core';
import type {
  AutorDoRepasse,
  EstadoDaPublicacao,
  NivelDaBiblioteca,
  PublicacaoComVendas,
  PublicacaoDaBiblioteca,
  PublicacaoDoPainel,
  TrabalhoDaBiblioteca,
  UsoDeIA,
} from './biblioteca';

/** Texto do editor livre, como a lista enxerga. */
export interface TextoSalvo {
  id: string;
  titulo: string;
  /** Começo do texto, sem tags — para a lista. */
  previa: string;
  caracteres: number;
  formato: FormatoDoTexto;
  criadoEm: string;
  atualizadoEm: string;
}

/** O texto completo, como o editor recebe. */
export interface TextoCompleto {
  id: string;
  titulo: string;
  conteudo: string;
  formato: FormatoDoTexto;
  criadoEm: string;
  atualizadoEm: string;
}

/** Texto de outra conta compartilhado com esta. */
export interface TextoCompartilhado extends TextoSalvo {
  podeEditar: boolean;
  dono: string;
}

/** Quem tem acesso a um texto. */
export interface CompartilhamentoDeTexto {
  usuarioId: string;
  nome: string;
  email: string;
  podeEditar: boolean;
  criadoEm: string;
}

/** Trecho bíblico como a API devolve (só domínio público). */
export interface TrechoDaBiblia {
  trecho: {
    livro: string;
    capitulo: number;
    versiculoInicial: number;
    versiculoFinal: number;
    versos: Array<{ numero: number; texto: string }>;
  };
  versao: string;
  traducao: string;
}
import { CATEGORIAS_PROMPT, ehIcone } from '@miolos/core';

export const BASE = ((import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3333').replace(/\/+$/, '');

export const CHAVE_TOKEN = 'miolos.token';

/** Documento acadêmico como a lista da área do cliente o mostra. */
export interface DocumentoSalvo {
  id: string;
  nome: string;
  modelo: ModeloDeDocumento;
  dados: DocumentoAcademico;
  resumo: {
    titulo: string;
    autores: string;
    modelo: string;
    figuras: number;
    referencias: number;
    secoes: number;
    itens: number;
  } | null;
  criadoEm: string;
  atualizadoEm: string;
}

/** O que o editor recebe do servidor ao abrir um documento. */
export interface DocumentoAberto {
  documento: DocumentoSalvo;
  avisos: AvisoDocumento[];
}

export class ErroApi extends Error {
  status: number;
  detalhes?: unknown;

  constructor(status: number, mensagem: string, detalhes?: unknown) {
    super(mensagem);
    this.name = 'ErroApi';
    this.status = status;
    this.detalhes = detalhes;
  }
}

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  /** Telefone com DDD, só dígitos (`11912345678`). Nulo quando não informado. */
  telefone?: string | null;
  admin?: boolean;
  /** Bônus da biblioteca de prompts de IA (administrador ou liberado por ele). */
  promptsIA?: boolean;
  /**
   * Editor de documentos liberado pelo administrador.
   *
   * Não vem do plano: é liberação nominal, como o bônus dos prompts. O site usa
   * para mostrar (ou esconder) o menu; a trava de verdade é do servidor.
   */
  editorDeDocumentos?: boolean;
  /**
   * Módulo de textos (o "Docs") liberado.
   *
   * Área **exclusiva**: nasce desligada e o administrador liga por conta. O site
   * usa para mostrar (ou esconder) o menu; a trava de verdade é do servidor.
   */
  editorDeTextos?: boolean;
  /**
   * Módulo de miolos (agenda). Padrão ligado; o administrador pode desligar para
   * oferecer só o editor acadêmico. O site esconde o menu quando desligado.
   */
  moduloMiolos?: boolean;
  ultimoAcessoEm?: string;
  criadoEm?: string;
  totalMiolos?: number;
}

/** A configuração da IA do usuário, como a tela a vê (sem a chave). */
export interface ConfiguracaoIA {
  configurada: boolean;
  provedor: 'DEEPSEEK' | 'OPENAI' | 'OLLAMA' | 'OUTRO';
  modelo: string;
  urlBase: string | null;
}

/** Usuário visto pelo painel do administrador. */
export interface UsuarioDoPainel {
  id: string;
  nome: string;
  email: string;
  /** Telefone com DDD, só dígitos. Nulo quando o cliente não informou. */
  telefone: string | null;
  admin: boolean;
  promptsIA: boolean;
  promptsIaLiberado: boolean;
  editorDeDocumentos: boolean;
  documentosLiberado: boolean;
  /** Módulo de textos liberado (área exclusiva, por convite). */
  editorDeTextos: boolean;
  textosLiberado: boolean;
  moduloMiolos: boolean;
  plano: string | null;
  licencaAtiva: boolean;
  licencaOrigem: string | null;
  licencaExpiraEm: string | null;
  totalMiolos: number;
  /** Miolos com PDF gerado: cada um consumiu um download do plano. */
  totalDownloads: number;
  totalPrompts: number;
  criadoEm: string;
  ultimoAcessoEm: string | null;
}

/** Convite de teste de um módulo, gerado pelo administrador. */
export interface Convite {
  id: string;
  codigo: string;
  modulo: 'MIOLOS' | 'DOCUMENTOS' | 'TEXTOS';
  usadoEm: string | null;
  criadoEm: string;
  criador?: { nome: string; email: string };
}

/** Sessão de demonstração liberada pela validação do e-mail. */
export interface SessaoDemo {
  token: string;
  email: string;
  nome: string;
  expiraEm: string;
  acessosRestantes: number;
  aviso: string;
}

export interface LeadFunil {
  id: string;
  email: string;
  nome: string;
  origem: string;
  status: 'SOLICITADO' | 'CONFIRMADO' | 'CONVERTIDO' | 'ABANDONADO';
  criadoEm: string;
  confirmadoEm: string | null;
  convertidoEm: string | null;
  expiraEm: string | null;
  acessosUsados: number;
  tentativas: number;
  ultimoEnvioEm: string | null;
  ultimoAcessoEm: string | null;
}

/** Carrinho parado: a pessoa gerou o link de pagamento e não pagou. */
export interface CarrinhoParado {
  id: string;
  nsu: string;
  planoId: string;
  nomePlano: string;
  valorCentavos: number;
  criadoEm: string;
  diasParado: number | null;
  ultimoContatoEm: string | null;
  diasDesdeOContato: number | null;
  /** Link do provedor, para reenviar exatamente o mesmo checkout. */
  linkCheckout: string | null;
  nome: string;
  email: string;
  telefone: string | null;
}

/** Quem testou a demonstração e não assinou. */
export interface DemonstracaoParada {
  id: string;
  nome: string;
  email: string;
  telefone: string | null;
  /** Quantos miolos a pessoa já criou — mede o quanto ela usou de verdade. */
  miolos: number;
  criadoEm: string;
  confirmadoEm: string | null;
  expiraEm: string | null;
  ultimoAcessoEm: string | null;
  ultimoEnvioEm: string | null;
  acessosUsados: number;
  diasParado: number | null;
}

export interface ResumoDaRecuperacao {
  carrinhos: number;
  valorParadoCentavos: number;
  demonstracoes: number;
  /** Vendas que voltaram depois de um contato nosso, nos últimos 30 dias. */
  recuperadas: number;
  valorRecuperadoCentavos: number;
}

export interface EventoSeguranca {
  id: string;
  tipo: string;
  severidade: 'BAIXA' | 'MEDIA' | 'ALTA';
  ip: string | null;
  rota: string | null;
  email: string | null;
  detalhe: string | null;
  criadoEm: string;
}

export interface MioloResumo {  id: string;
  nome: string;
  resumo: ResumoMiolo | null;
  avisos: AvisoMiolo[] | null;
  criadoEm: string;
  atualizadoEm: string;
}

export interface MioloCompleto extends MioloResumo {
  config: ConfigPlanner;
  versaoConfig: number;
}

export interface ArquivoEnviado {
  id: string;
  url: string;
  nome: string;
  bytes: number;
  criadoEm?: string;
}

export function lerToken(): string | null {
  try {
    return window.localStorage.getItem(CHAVE_TOKEN);
  } catch {
    return null;
  }
}

export function gravarToken(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(CHAVE_TOKEN, token);
    else window.localStorage.removeItem(CHAVE_TOKEN);
  } catch {
    /* modo privado do navegador: segue sem persistir */
  }
}

interface OpcoesRequisicao {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  corpo?: unknown;
  formData?: FormData;
  autenticado?: boolean;
  sinal?: AbortSignal;
}

async function requisicao<T>(caminho: string, opcoes: OpcoesRequisicao = {}): Promise<T> {
  const { metodo = 'GET', corpo, formData, autenticado = true, sinal } = opcoes;

  const cabecalhos: Record<string, string> = {};
  if (autenticado) {
    const token = lerToken();
    if (token) cabecalhos.Authorization = `Bearer ${token}`;
  }
  if (corpo !== undefined) cabecalhos['Content-Type'] = 'application/json';

  let resposta: Response;
  try {
    resposta = await fetch(`${BASE}/api${caminho}`, {
      method: metodo,
      headers: cabecalhos,
      body: formData ?? (corpo !== undefined ? JSON.stringify(corpo) : undefined),
      signal: sinal,
    });
  } catch {
    throw new ErroApi(0, 'Não conseguimos falar com o servidor. Verifique se a API está rodando.');
  }

  if (resposta.status === 204) return undefined as T;

  const texto = await resposta.text();
  const dados: unknown = texto ? safeJson(texto) : null;

  if (!resposta.ok) {
    const corpoErro = (dados ?? {}) as { mensagem?: string; erro?: string };
    if (resposta.status === 401) gravarToken(null);
    throw new ErroApi(
      resposta.status,
      corpoErro.mensagem || corpoErro.erro || 'Não foi possível concluir a operação.',
      dados,
    );
  }

  return dados as T;
}

function safeJson(texto: string): unknown {
  try {
    return JSON.parse(texto);
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Endpoints                                                           */
/* ------------------------------------------------------------------ */

export const api = {
  /* ---- autenticação ---- */
  registrar: (dados: { nome: string; email: string; senha: string; telefone?: string }) =>
    requisicao<{ token: string; usuario: Usuario }>('/auth/registrar', {
      metodo: 'POST',
      corpo: dados,
      autenticado: false,
    }),

  entrar: (dados: { email: string; senha: string }) =>
    requisicao<{ token: string; usuario: Usuario }>('/auth/entrar', {
      metodo: 'POST',
      corpo: dados,
      autenticado: false,
    }),

  perfil: () => requisicao<Usuario>('/auth/eu'),

  atualizarPerfil: (dados: { nome?: string; telefone?: string | null }) =>
    requisicao<Usuario>('/auth/eu', { metodo: 'PATCH', corpo: dados }),

  /* ---- recuperação de senha ---- */
  esqueciSenha: (email: string) =>
    requisicao<{ ok: boolean }>('/auth/esqueci-senha', {
      metodo: 'POST',
      corpo: { email },
      autenticado: false,
    }),

  redefinirSenha: (dados: { codigo: string; senha: string }) =>
    requisicao<{ ok: boolean }>('/auth/redefinir-senha', {
      metodo: 'POST',
      corpo: dados,
      autenticado: false,
    }),

  enviarRecuperacaoDeSenha: (usuarioId: string) =>
    requisicao<{ ok: boolean; entregue: boolean; aviso?: string }>(
      `/admin/usuarios/${usuarioId}/enviar-recuperacao`,
      { metodo: 'POST' },
    ),

  /* ---- IA do usuário ---- */
  configuracaoIA: () => requisicao<ConfiguracaoIA>('/ia'),

  salvarConfiguracaoIA: (dados: {
    apiKey?: string;
    provedor?: 'DEEPSEEK' | 'OPENAI' | 'OLLAMA' | 'OUTRO';
    modelo?: string;
    urlBase?: string;
  }) => requisicao<ConfiguracaoIA>('/ia', { metodo: 'PUT', corpo: dados }),

  traduzirTexto: (dados: { texto: string; idioma: string }) =>
    requisicao<{ traducao: string }>('/ia/traduzir', { metodo: 'POST', corpo: dados }),

  /* ---- miolos ---- */
  listarMiolos: () => requisicao<{ miolos: MioloResumo[] }>('/miolos'),

  criarMiolo: (dados: { nome: string; config: ConfigPlanner }) =>
    requisicao<{ miolo: MioloCompleto }>('/miolos', { metodo: 'POST', corpo: dados }),

  obterMiolo: (id: string) => requisicao<{ miolo: MioloCompleto }>(`/miolos/${id}`),

  atualizarMiolo: (id: string, dados: { nome?: string; config?: ConfigPlanner }) =>
    requisicao<{ miolo: MioloCompleto }>(`/miolos/${id}`, { metodo: 'PUT', corpo: dados }),

  duplicarMiolo: (id: string, proximoAno = false) =>
    requisicao<{ miolo: MioloCompleto }>(`/miolos/${id}/duplicar`, {
      metodo: 'POST',
      corpo: { proximoAno },
    }),
  excluirMiolo: (id: string) => requisicao<void>(`/miolos/${id}`, { metodo: 'DELETE' }),

  /* ---- PDF gerado no servidor ---- */

  /**
   * Pede o PDF ao servidor.
   *
   * A resposta chega **na hora**: `PRONTO` quando o arquivo guardado ainda vale
   * (o site baixa na sequência) e `GERANDO` quando a geração entrou na fila — o
   * desenho segue no servidor e quem acompanha é o `estadoDoPdf`.
   */
  gerarPdf: (id: string) =>
    requisicao<{
      estado: 'PRONTO' | 'GERANDO';
      reaproveitado?: boolean;
      bytes?: number;
      paginas?: number;
      url: string;
    }>(`/miolos/${id}/pdf`, { metodo: 'POST' }),

  /** Estado da fila do PDF: é o que permite sair da página e voltar depois. */
  estadoDoPdf: (id: string) =>
    requisicao<{
      estado: 'NUNCA' | 'GERANDO' | 'PRONTO' | 'FALHOU';
      paginas: number | null;
      bytes: number | null;
      /** O arquivo guardado é mais novo que a última edição do miolo. */
      atualizado: boolean;
      geradoEm: string | null;
      iniciadoEm: string | null;
      erro: string | null;
      atualizadoEm: string;
      url: string;
    }>(`/miolos/${id}/pdf/estado`),

  /** Baixa o PDF já gerado, com o nome do miolo no arquivo. */
  baixarPdf: async (id: string, nomeDoMiolo: string): Promise<void> => {
    const resposta = await fetch(`${BASE}/api/miolos/${id}/pdf`, {
      headers: { Authorization: `Bearer ${lerToken() ?? ''}` },
    });

    if (!resposta.ok) {
      throw new ErroApi(resposta.status, 'Não conseguimos baixar o PDF agora.');
    }

    const arquivo = await resposta.blob();
    const endereco = URL.createObjectURL(arquivo);
    const link = document.createElement('a');
    link.href = endereco;
    link.download = `${nomeDoMiolo.replace(/[^\p{L}\p{N} ._-]+/gu, '').trim() || 'miolo'}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(endereco);
  },

  /* ---- imagem de divulgação (mockup) ---- */

  /**
   * Pede a imagem de divulgação e salva o arquivo.
   *
   * O desenho é do servidor (mesmo Chromium do PDF), e vem pronto na resposta: não
   * fica guardado e não tem estado — é outro arquivo, para outra finalidade
   * ([[Impressão e PDF]] e a nota da imagem de divulgação no cofre).
   */
  baixarMockup: async (
    id: string,
    nomeDoMiolo: string,
    opcoes: { formato: string; fundo: string; cena: string; assinatura: string | null },
  ): Promise<void> => {
    const resposta = await fetch(`${BASE}/api/miolos/${id}/mockup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${lerToken() ?? ''}`,
      },
      body: JSON.stringify(opcoes),
    });

    if (!resposta.ok) {
      throw new ErroApi(resposta.status, 'Não conseguimos montar a imagem agora.');
    }

    const arquivo = await resposta.blob();
    const endereco = URL.createObjectURL(arquivo);
    const link = document.createElement('a');
    link.href = endereco;
    link.download = `${nomeDoMiolo.replace(/[^\p{L}\p{N} ._-]+/gu, '').trim() || 'miolo'}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(endereco);
  },

  /* ---- arquivos ---- */
  listarArquivos: () => requisicao<{ arquivos: ArquivoEnviado[] }>('/arquivos'),

  enviarImagem: (arquivo: File) => {
    const dados = new FormData();
    dados.append('arquivo', arquivo);
    return requisicao<{ arquivo: ArquivoEnviado }>('/arquivos', { metodo: 'POST', formData: dados });
  },

  /** Envia um PDF de anexo (o mesmo endpoint das imagens, que agora aceita PDF). */
  enviarArquivo: (arquivo: File) => {
    const dados = new FormData();
    dados.append('arquivo', arquivo);
    return requisicao<{ arquivo: ArquivoEnviado }>('/arquivos', { metodo: 'POST', formData: dados });
  },

  excluirArquivo: (id: string) => requisicao<void>(`/arquivos/${id}`, { metodo: 'DELETE' }),

  /* ---- planos e pagamento ---- */
  listarPlanos: () => requisicao<CatalogoPlanos>('/planos', { autenticado: false }),

  minhaLicenca: () => requisicao<EstadoLicenca>('/minha-licenca'),

  criarPedido: (planoId: string) =>
    requisicao<RespostaPedido>('/pedidos', { metodo: 'POST', corpo: { planoId } }),

  listarPedidos: () => requisicao<{ pedidos: Pedido[] }>('/pedidos'),

  confirmarPedido: (nsu: string) =>
    requisicao<{ pedido: Pedido; jaEstavaPago?: boolean }>(`/pedidos/${nsu}/confirmar`, {
      metodo: 'POST',
    }),

  /**
   * Confere no InfinitePay se o pedido já consta como pago (histórico da
   * transação) e libera a licença sem depender do webhook.
   */
  verificarPagamento: (nsu: string) =>
    requisicao<ResultadoVerificacao>(`/pedidos/${nsu}/verificar`, { metodo: 'POST' }),

  /* ---- pedidos (administrador) ---- */
  listarPedidosDoPainel: () =>
    requisicao<{ pedidos: PedidoDoPainel[]; conciliacao: EstadoConciliacao }>('/admin/pedidos'),

  conciliarPagamentos: () =>
    requisicao<ResumoConciliacao>('/admin/pedidos/conciliar', { metodo: 'POST' }),

  /* ---- planos (administrador) ---- */
  /** Catálogo completo, com os ajustes aplicados e inclusive os planos escondidos. */
  listarPlanosDoPainel: () => requisicao<{ planos: PlanoDoPainel[] }>('/admin/planos'),

  /**
   * Edita, esconde ou mostra um plano. `ajustes: null` devolve o plano ao
   * padrão do catálogo; `oculto: true` tira o plano do site.
   */
  salvarPlano: (id: string, dados: { oculto?: boolean; ajustes?: AjustesDePlano | null }) =>
    requisicao<{ plano: PlanoDoPainel }>(`/admin/planos/${encodeURIComponent(id)}`, {
      metodo: 'PATCH',
      corpo: dados,
    }),

  /* ---- fluxo de criação ---- */
  /** Lista o que o administrador liberou (público: o assistente precisa disso). */
  listarRecursos: () => requisicao<{ recursos: RecursoDoFluxo[] }>('/recursos', { autenticado: false }),

  listarFluxoDoPainel: () =>
    requisicao<{ recursos: RecursoDoFluxo[]; resumo: ResumoDoFluxo[] }>('/admin/fluxo'),

  definirRecurso: (chave: string, liberado: boolean) =>
    requisicao<{ recurso: RecursoDoFluxo; resumo: ResumoDoFluxo[] }>(`/admin/recursos/${encodeURIComponent(chave)}`, {
      metodo: 'PATCH',
      corpo: { liberado },
    }),

  /* ---- demonstração ---- */
  solicitarDemo: (dados: { nome?: string; email: string }) =>
    requisicao<{ ok: true; mensagem: string; via: string; aviso?: string }>('/demo/solicitar', {
      metodo: 'POST',
      corpo: dados,
      autenticado: false,
    }),

  confirmarDemo: (token: string) =>
    requisicao<SessaoDemo & { ok: true; demo: true }>('/demo/confirmar', {
      metodo: 'POST',
      corpo: { token },
      autenticado: false,
    }),

  reenviarDemo: (email: string) =>
    requisicao<{ ok: true; mensagem: string }>('/demo/reenviar', {
      metodo: 'POST',
      corpo: { email },
      autenticado: false,
    }),

  /* ---- administração (funil e segurança) ---- */
  funil: (filtros: { status?: string; busca?: string; limite?: number } = {}) => {
    const parametros = new URLSearchParams();
    if (filtros.status) parametros.set('status', filtros.status);
    if (filtros.busca) parametros.set('busca', filtros.busca);
    if (filtros.limite) parametros.set('limite', String(filtros.limite));
    const consulta = parametros.toString();
    return requisicao<{
      total: number;
      resumo: Record<string, number>;
      limiteDeAcessosPorEmail: number;
      leads: LeadFunil[];
    }>(`/admin/funil${consulta ? `?${consulta}` : ''}`);
  },

  atualizarLead: (id: string, status: LeadFunil['status']) =>
    requisicao<LeadFunil>(`/admin/funil/${id}`, { metodo: 'PATCH', corpo: { status } }),

  enviarLembrete: (id: string) =>
    requisicao<{ ok: true; via: string; aviso?: string }>(`/admin/funil/${id}/lembrete`, {
      metodo: 'POST',
    }),

  eventosDeSeguranca: (limite = 60) =>
    requisicao<{
      bloqueiosAtivos: number;
      resumo: Array<{ tipo: string; total: number }>;
      eventos: EventoSeguranca[];
    }>(`/admin/seguranca?limite=${limite}`),

  /* ---- biblioteca de prompts ---- */
  guiaDePrompts: () =>
    requisicao<{
      guia: Array<{ id: string; titulo: string; paragrafos: string[]; itens?: string[] }>;
      categorias: Array<{ id: CategoriaPrompt; nome: string }>;
      campos: string[];
      exemplos: PromptExemplo[];
    }>('/prompts/guia'),

  listarPrompts: (filtros: { busca?: string; categoria?: CategoriaPrompt; favoritos?: boolean } = {}) => {
    const parametros = new URLSearchParams();
    if (filtros.busca) parametros.set('busca', filtros.busca);
    if (filtros.categoria) parametros.set('categoria', filtros.categoria);
    if (filtros.favoritos) parametros.set('favoritos', 'true');
    const consulta = parametros.toString();
    return requisicao<{
      prompts: PromptSalvo[];
      total: number;
      limite: number;
      porCategoria: Record<string, number>;
    }>(`/prompts${consulta ? `?${consulta}` : ''}`);
  },

  criarPrompt: (dados: DadosPrompt) =>
    requisicao<{ prompt: PromptSalvo }>('/prompts', { metodo: 'POST', corpo: dados }),

  atualizarPrompt: (id: string, dados: Partial<DadosPrompt>) =>
    requisicao<{ prompt: PromptSalvo }>(`/prompts/${id}`, { metodo: 'PUT', corpo: dados }),

  marcarUsoDoPrompt: (id: string) =>
    requisicao<{ prompt: PromptSalvo }>(`/prompts/${id}/usar`, { metodo: 'POST' }),

  excluirPrompt: (id: string) => requisicao<void>(`/prompts/${id}`, { metodo: 'DELETE' }),

  /* ---- administração de usuários ---- */
  listarUsuarios: (busca?: string) =>
    requisicao<{ usuarios: UsuarioDoPainel[] }>(
      `/admin/usuarios${busca ? `?busca=${encodeURIComponent(busca)}` : ''}`,
    ),

  definirBonusPrompts: (id: string, liberado: boolean) =>
    requisicao<{ usuario: UsuarioDoPainel }>(`/admin/usuarios/${id}/prompts`, {
      metodo: 'PATCH',
      corpo: { liberado },
    }),

  /**
   * Corrige o nome e o telefone de uma conta. O e-mail de acesso não muda por
   * aqui, e o telefone vazio (`null`) apaga o número.
   */
  editarUsuario: (id: string, dados: { nome?: string; telefone?: string | null }) =>
    requisicao<{ usuario: { id: string; nome: string; email: string; telefone: string | null } }>(
      `/admin/usuarios/${id}`,
      { metodo: 'PATCH', corpo: dados },
    ),

  /* ---- recuperação de vendas ---- */
  /** Quem ficou com a compra pela metade: carrinhos parados e demonstrações sem assinatura. */
  recuperacao: () =>
    requisicao<{
      resumo: ResumoDaRecuperacao;
      carrinhos: CarrinhoParado[];
      demonstracoes: DemonstracaoParada[];
    }>('/admin/recuperacao'),

  /** Envia a mensagem de recuperação e anota a data do contato. */
  contatarRecuperacao: (tipo: 'carrinho' | 'demonstracao', id: string) =>
    requisicao<{ mensagem: string; envio: { via: 'SMTP' | 'OUTBOX'; entregue: boolean; aviso?: string } }>(
      `/admin/recuperacao/${tipo}/${id}/contato`,
      { metodo: 'POST' },
    ),

  /** Marca o pedido como perdido (sai da recuperação) ou reabre para tentar de novo. */
  definirStatusDoPedido: (id: string, status: 'PENDENTE' | 'CANCELADO') =>
    requisicao<{ pedido: { id: string; nsu: string; status: string } }>(`/admin/pedidos/${id}`, {
      metodo: 'PATCH',
      corpo: { status },
    }),

  definirAcesso: (
    id: string,
    dados: { planoId?: string; ativa?: boolean; expiraEm?: string | null; origem?: 'MANUAL' | 'CORTESIA' },
  ) =>
    requisicao<{ licenca: unknown }>(`/admin/usuarios/${id}/licenca`, {
      metodo: 'PATCH',
      corpo: dados,
    }),

  /* ---- editor de documentos acadêmicos ---- */

  listarDocumentos: () => requisicao<{ documentos: DocumentoSalvo[] }>('/documentos'),

  /**
   * "Quero conhecer": pega o acesso gratuito ao editor para a conta logada.
   *
   * É a porta de quem chega pela área de quem escreve: o servidor dá o plano
   * gratuito do catálogo e o acesso sai na hora (ver `POST /api/documentos/acesso-gratuito`).
   */
  pedirAcessoAoEditor: () =>
    requisicao<{ ok: boolean; jaTinha: boolean; editorDeDocumentos: boolean; plano?: string }>(
      '/documentos/acesso-gratuito',
      { metodo: 'POST' },
    ),

  criarDocumento: (nome: string, modelo: ModeloDeDocumento, perfil?: string) =>
    requisicao<{ documento: DocumentoSalvo }>('/documentos', {
      metodo: 'POST',
      corpo: { nome, modelo, perfil },
    }),

  abrirDocumento: (id: string) => requisicao<DocumentoAberto>(`/documentos/${id}`),

  /**
   * As referências que vêm de fora: BibTeX, RIS ou um DOI.
   *
   * O texto vai inteiro para o servidor (é lá que se fala com o Crossref, quando o que
   * veio é um DOI): a resposta traz as referências sem id — quem entra no documento é a
   * tela, que dá o id de cada uma.
   */
  importarReferencias: (texto: string) =>
    requisicao<{
      referencias: Array<Omit<ReferenciaDocumento, 'id'>>;
      formato: 'BIBTEX' | 'RIS' | 'DOI' | 'ISBN';
      doi?: string;
      isbn?: string;
    }>('/documentos/referencias/importar', { metodo: 'POST', corpo: { texto } }),

  buscarReferencias: (titulo: string) =>
    requisicao<{
      candidatos: Array<{
        referencia: Omit<ReferenciaDocumento, 'id'>;
        fonte: 'CROSSREF' | 'OPENLIBRARY';
      }>;
      maximo: number;
    }>('/documentos/referencias/buscar', { metodo: 'POST', corpo: { titulo } }),

  salvarDocumento: (id: string, documento: Partial<DocumentoAcademico>, nome?: string) =>
    requisicao<DocumentoAberto>(`/documentos/${id}`, {
      metodo: 'PUT',
      corpo: { documento, nome },
    }),

  duplicarDocumento: (id: string) =>
    requisicao<{ documento: DocumentoSalvo }>(`/documentos/${id}/duplicar`, { metodo: 'POST' }),

  apagarDocumento: (id: string) => requisicao<{ ok: boolean }>(`/documentos/${id}`, { metodo: 'DELETE' }),

  definirEditorDeDocumentos: (id: string, liberado: boolean) =>
    requisicao<{ usuario: UsuarioDoPainel }>(`/admin/usuarios/${id}/documentos`, {
      metodo: 'PATCH',
      corpo: { liberado },
    }),

  /* ---- módulos (miolos × documentos) ---- */
  definirModuloMiolos: (id: string, liberado: boolean) =>
    requisicao<{ usuario: { id: string; moduloMiolos: boolean } }>(`/admin/usuarios/${id}/miolos`, {
      metodo: 'PATCH',
      corpo: { liberado },
    }),

  /* ---- editor de textos livres ---- */
  listarTextos: () =>
    requisicao<{ textos: TextoSalvo[]; compartilhados: TextoCompartilhado[] }>('/textos'),

  abrirTexto: (id: string) =>
    requisicao<{ texto: TextoCompleto; meu: boolean; podeEditar: boolean }>(`/textos/${id}`),

  /** Quem tem acesso ao texto (só o dono). */
  listarCompartilhamentos: (id: string) =>
    requisicao<{ compartilhamentos: CompartilhamentoDeTexto[] }>(`/textos/${id}/compartilhamentos`),

  compartilharTexto: (id: string, dados: { email: string; podeEditar: boolean }) =>
    requisicao<{ compartilhamento: CompartilhamentoDeTexto }>(`/textos/${id}/compartilhamentos`, {
      metodo: 'POST',
      corpo: dados,
    }),

  removerCompartilhamento: (id: string, usuarioId: string) =>
    requisicao<{ ok: boolean }>(`/textos/${id}/compartilhamentos/${usuarioId}`, { metodo: 'DELETE' }),

  /** Trecho da Bíblia (João Ferreira de Almeida, domínio público). */
  trechoDaBiblia: (referencia: string) =>
    requisicao<TrechoDaBiblia>(`/biblia/trecho?referencia=${encodeURIComponent(referencia)}`),

  definirEditorDeTextos: (id: string, liberado: boolean) =>
    requisicao<{ usuario: UsuarioDoPainel }>(`/admin/usuarios/${id}/textos`, {
      metodo: 'PATCH',
      corpo: { liberado },
    }),

  criarTexto: (dados: {
    titulo?: string;
    conteudo?: string;
    formato?: Partial<FormatoDoTexto>;
    /** Cria o texto a partir de um modelo do catálogo (ata, relatório, carta...). */
    modelo?: { id: string; valores?: Record<string, string> };
  }) => requisicao<{ texto: TextoCompleto }>('/textos', { metodo: 'POST', corpo: dados }),

  salvarTexto: (
    id: string,
    dados: { titulo?: string; conteudo?: string; formato?: Partial<FormatoDoTexto> },
  ) => requisicao<{ texto: TextoCompleto }>(`/textos/${id}`, { metodo: 'PUT', corpo: dados }),

  apagarTexto: (id: string) => requisicao<{ ok: boolean }>(`/textos/${id}`, { metodo: 'DELETE' }),

  duplicarTexto: (id: string) =>
    requisicao<{ texto: TextoCompleto }>(`/textos/${id}/duplicar`, { metodo: 'POST' }),

  /* ---- biblioteca de trabalhos ---- */
  listarBiblioteca: (opcoes: { busca?: string; nivel?: string } = {}) => {
    const parametros = new URLSearchParams();
    if (opcoes.busca?.trim()) parametros.set('busca', opcoes.busca.trim());
    if (opcoes.nivel) parametros.set('nivel', opcoes.nivel);
    const consulta = parametros.toString();
    return requisicao<{ publicacoes: PublicacaoDaBiblioteca[] }>(
      `/biblioteca${consulta ? `?${consulta}` : ''}`,
      { autenticado: false },
    );
  },

  abrirTrabalho: (id: string) => requisicao<TrabalhoDaBiblioteca>(`/biblioteca/${id}`),

  /** O trabalho inteiro — responde 402 quando falta a consulta. */
  lerTrabalho: (id: string) =>
    requisicao<{ documento: DocumentoAcademico; nome: string }>(`/biblioteca/${id}/texto`),

  publicarNaBiblioteca: (dados: {
    documentoId: string;
    nivel: NivelDaBiblioteca;
    usaIA: UsoDeIA;
    comoUsouIA?: string;
    contribuicaoAtiva: boolean;
    contribuicaoSugeridaCentavos?: number;
    aceitaTermo: true;
  }) => requisicao<{ publicacao: PublicacaoDaBiblioteca }>('/biblioteca', { metodo: 'POST', corpo: dados }),

  minhasPublicacoes: () =>
    requisicao<{ publicacoes: PublicacaoComVendas[] }>('/biblioteca/minhas'),

  atualizarPublicacao: (
    id: string,
    dados: {
      contribuicaoAtiva?: boolean;
      contribuicaoSugeridaCentavos?: number | null;
      arquivar?: boolean;
    },
  ) => requisicao<{ publicacao: PublicacaoDaBiblioteca }>(`/biblioteca/${id}`, { metodo: 'PATCH', corpo: dados }),

  minhasConsultas: () =>
    requisicao<{
      consultas: Array<{
        id: string;
        valorCentavos: number;
        contribuicaoCentavos: number;
        pagoEm: string | null;
        publicacao: { id: string; titulo: string; autores: string };
      }>;
    }>('/biblioteca/consultas'),

  consultarTrabalho: (id: string, contribuicaoCentavos?: number) =>
    requisicao<
      | { gratuito: true }
      | { consulta: { id: string; nsu: string }; url: string; aproximado: boolean; aviso?: string }
    >(`/biblioteca/${id}/consulta`, { metodo: 'POST', corpo: { contribuicaoCentavos } }),

  confirmarConsulta: (nsu: string) =>
    requisicao<{ pago: boolean; jaEstavaPago?: boolean; encontrado?: boolean }>(
      `/biblioteca/consultas/${nsu}/confirmar`,
      { metodo: 'POST' },
    ),

  /* ---- biblioteca (administrador) ---- */
  bibliotecaDoPainel: (estado?: string) =>
    requisicao<{ publicacoes: PublicacaoDoPainel[] }>(
      `/admin/biblioteca${estado ? `?estado=${estado}` : ''}`,
    ),

  moderarPublicacao: (id: string, dados: { estado?: EstadoDaPublicacao; gratuito?: boolean; motivo?: string | null }) =>
    requisicao<{ publicacao: PublicacaoDaBiblioteca }>(`/admin/biblioteca/${id}`, {
      metodo: 'PATCH',
      corpo: dados,
    }),

  repassesDaBiblioteca: () => requisicao<{ autores: AutorDoRepasse[] }>('/admin/biblioteca/repasses'),

  marcarRepasse: (usuarioId: string) =>
    requisicao<{ marcadas: number }>(`/admin/biblioteca/repasses/${usuarioId}`, { metodo: 'POST' }),

  /* ---- convites de teste por módulo ---- */
  listarConvites: () => requisicao<{ convites: Convite[] }>('/admin/convites'),

  criarConvite: (modulo: 'MIOLOS' | 'DOCUMENTOS' | 'TEXTOS') =>
    requisicao<{ convite: Convite }>('/admin/convites', { metodo: 'POST', corpo: { modulo } }),

  excluirConvite: (id: string) => requisicao<void>(`/admin/convites/${id}`, { metodo: 'DELETE' }),

  consultarConvite: (codigo: string) =>
    requisicao<{ modulo: 'MIOLOS' | 'DOCUMENTOS'; usado: boolean }>(`/convites/${codigo}`),

  usarConvite: (codigo: string) =>
    requisicao<{ modulo: 'MIOLOS' | 'DOCUMENTOS' }>(`/convites/${codigo}/usar`, { metodo: 'POST' }),

  /* ---- contato com o desenvolvedor ---- */
  enviarContato: (dados: { nome: string; email: string; mensagem: string }) =>
    requisicao<{ ok: boolean; id: string }>('/contato', {
      metodo: 'POST',
      corpo: dados,
      autenticado: false,
    }),

  listarContatos: () =>
    requisicao<{ contatos: Array<{ id: string; nome: string; email: string; mensagem: string; criadoEm: string; lido: boolean }> }>(
      '/admin/contatos',
    ),

  /* ---- modo manutenção ---- */
  estadoDaManutencao: () => requisicao<{ ativa: boolean; mensagem: string }>('/manutencao', { autenticado: false }),

  salvarManutencao: (dados: { ativa: boolean; mensagem: string }) =>
    requisicao<{ ativa: boolean; mensagem: string }>('/admin/manutencao', { metodo: 'PUT', corpo: dados }),

  /* ---- configurações do administrador ---- */
  configuracoesDoAdmin: () =>
    requisicao<{ tagDoInfinitePay: string; tagEmUso: string }>('/admin/configuracoes'),

  salvarTagDoInfinitePay: (tag: string) =>
    requisicao<{ tagDoInfinitePay: string; tagEmUso: string }>('/admin/configuracoes/infinitepay-tag', {
      metodo: 'PUT',
      corpo: { tag },
    }),
};

/* ------------------------------------------------------------------ */
/* Prompts                                                             */
/* ------------------------------------------------------------------ */

export type CategoriaPrompt = (typeof CATEGORIAS_PROMPT)[number];

export interface PromptSalvo {
  id: string;
  titulo: string;
  categoria: CategoriaPrompt;
  objetivo: string;
  conteudo: string;
  modelo: string;
  resultado: string;
  tags: string[];
  favorito: boolean;
  usos: number;
  criadoEm: string;
  atualizadoEm: string;
}

export interface DadosPrompt {
  titulo: string;
  categoria: CategoriaPrompt;
  objetivo: string;
  conteudo: string;
  modelo: string;
  resultado: string;
  tags: string[];
  favorito: boolean;
}

export interface PromptExemplo {
  id: string;
  titulo: string;
  categoria: CategoriaPrompt;
  objetivo: string;
  conteudo: string;
  dica: string;
}

/* ------------------------------------------------------------------ */
/* Comercial                                                           */
/* ------------------------------------------------------------------ */

export interface LimitesPlano {
  miolos: number;
  /** A cota vale para o total da licença (`TOTAL`) ou por ciclo (`MES`). */
  periodo: PeriodoCota;
  cadernosPorMiolo: number;
  camadasPorMiolo: number;
  cadernosAvancados: boolean;
  usoComercial: boolean;
  suportePrioritario: boolean;
  atualizacoesVitalicias: boolean;
}

export interface Plano {
  id: string;
  nome: string;
  resumo: string;
  /**
   * Como este plano é cobrado — o site mostra o rótulo do próprio plano
   * ("por mês", "pagamento único", "por miolo") embaixo do preço.
   */
  cobranca: TipoCobranca;
  precoCentavos: number;
  precoFormatado: string;
  precoDeCentavos?: number;
  precoDeFormatado: string | null;
  destaque?: boolean;
  selo?: string;
  publico: string;
  recursos: string[];
  limites: LimitesPlano;
}

export interface CatalogoPlanos {
  planos: Plano[];
  provedor: { nome: string; tag: string; formas: string[] };
  confirmacaoManual: boolean;
}

export interface Pedido {
  id: string;
  nsu: string;
  planoId: string;
  planoNome?: string;
  valorCentavos: number;
  valorFormatado?: string;
  status: 'PENDENTE' | 'PAGO' | 'CANCELADO';
  metodo?: string | null;
  metodoDescricao?: string | null;
  receiptUrl?: string | null;
  linkCheckout?: string | null;
  linkAproximado?: boolean;
  aviso?: string | null;
  pagoEm?: string | null;
  criadoEm: string;
}

export interface RespostaPedido {
  pedido: Pedido;
  urlCheckout: string;
  aproximado: boolean;
  aviso: string | null;
}

/** Resposta da conferência do pagamento no provedor. */
export interface ResultadoVerificacao {
  situacao: 'PAGO' | 'PENDENTE' | 'INDISPONIVEL';
  jaEstavaPago: boolean;
  aviso: string | null;
  licenca: Licenca | null;
  conciliacaoAutomatica: boolean;
  intervaloMinutos: number;
}

/** Pedido como o painel do administrador enxerga. */
export interface PedidoDoPainel {
  id: string;
  nsu: string;
  planoId: string;
  nomePlano: string;
  status: 'PENDENTE' | 'PAGO' | 'CANCELADO';
  valorCentavos: number;
  metodo: string | null;
  transactionNsu: string | null;
  receiptUrl: string | null;
  criadoEm: string;
  pagoEm: string | null;
  nome: string;
  email: string;
}

export interface EstadoConciliacao {
  ativa: boolean;
  intervaloMinutos: number;
  dias: number;
}

export interface ResumoConciliacao {
  verificados: number;
  confirmados: number;
  pendentes: number;
  indisponiveis: number;
  pedidos: Array<{ nsu: string; situacao: 'PAGO' | 'PENDENTE' | 'INDISPONIVEL'; aviso: string | null }>;
}

/** Plano do catálogo como o painel enxerga: com os ajustes e o estado de exibição. */
export interface PlanoDoPainel extends PlanoDoCatalogo {
  /** true = fora do site, nem aparece para comprar. */
  oculto: boolean;
  /** Só o que foi sobrescrito no painel (null = plano no padrão do catálogo). */
  ajustes: AjustesDePlano | null;
  /** Quantas licenças usam este plano — útil antes de esconder. */
  assinantes: number;
}

/** Item do fluxo de criação (passo do assistente ou recurso personalizável). */
export interface RecursoDoFluxo {
  chave: string;
  titulo: string;
  descricao: string;
  grupo: string;
  ordem: number;
  liberado: boolean;
}

export interface ResumoDoFluxo {
  grupo: string;
  total: number;
  liberados: number;
}

export interface Licenca {
  id: string;
  planoId: string;
  ativa: boolean;
  origem: string;
  ativadaEm: string;
  plano: Plano;
}

export interface EstadoLicenca {
  /** true = as páginas exportadas saem com marca de "cópia não autorizada". */
  marcaDagua: boolean;
  /** true = o administrador bloqueou este acesso: não gera novos miolos. */
  bloqueado?: boolean;
  licenca: Licenca | null;
  uso: {
    miolos: number;
    /** Miolos já baixados (PDF gerado). Cada download consome um miolo do plano. */
    downloads: number;
    limiteMiolos: number | null;
    limiteDownloads: number | null;
  };
  planos: Array<{ id: string; nome: string; precoCentavos: number }>;
}

/** Converte `/uploads/...` na URL absoluta da API. */
export function urlDaImagem(url: string): string {
  if (!url) return url;
  // Ícones da biblioteca são desenhados no próprio navegador: não têm URL.
  if (ehIcone(url)) return url;
  if (/^(https?:|data:|blob:)/.test(url)) return url;
  return `${BASE}${url}`;
}

export const urlApi = BASE;
